import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

export interface ProdutoFornecedorRow {
  id: number;
  fornecedorId: number;
  fornecedorNome: string;
  codigoProdutoFornecedor?: string | null;
  codigoBarrasFornecedor?: string | null;
  precoCompra?: number | null;
  lucroEsperado?: number | null;
  custos?: number | null;
  multiplicaDivide: boolean;
}

export interface SalvarFornecedorRequest {
  fornecedorId: number;
  codigoProdutoFornecedor?: string | null;
  codigoBarrasFornecedor?: string | null;
  precoCompra?: number | null;
  lucroEsperado?: number | null;
  custos?: number | null;
  multiplicaDivide: boolean;
}

/** Pessoas marcadas como Fornecedor (combo). */
export const useFornecedoresCombo = () => {
  const { data = [] } = useQuery({
    queryKey: ["clientes-fornecedores"],
    queryFn: async () => (await apiClient.get<{ id: number; nomeRazao: string }[]>("/api/clientes/fornecedores")).data,
    staleTime: 5 * 60 * 1000,
  });
  return data;
};

export const useProdutoFornecedores = (produtoId: string | undefined) => {
  const qc = useQueryClient();
  const numId = produtoId ? Number(produtoId) : undefined;
  const key = ["produto-fornecedores", numId];
  const base = `/api/produtos/${numId}/fornecedores`;

  const query = useQuery({
    queryKey: key,
    queryFn: async () => (await apiClient.get<ProdutoFornecedorRow[]>(base)).data,
    enabled: !!numId,
  });

  const create = useMutation({
    mutationFn: async (body: SalvarFornecedorRequest) => (await apiClient.post<ProdutoFornecedorRow>(base, body)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); toast.success("Fornecedor vinculado!"); },
    onError: (e: Error) => toast.error(`Erro: ${e.message}`),
  });

  const update = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: SalvarFornecedorRequest }) =>
      (await apiClient.put<ProdutoFornecedorRow>(`${base}/${id}`, data)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); toast.success("Fornecedor atualizado!"); },
    onError: (e: Error) => toast.error(`Erro: ${e.message}`),
  });

  const remove = useMutation({
    mutationFn: async (id: number) => { await apiClient.delete(`${base}/${id}`); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); toast.success("Fornecedor desvinculado!"); },
    onError: (e: Error) => toast.error(`Erro: ${e.message}`),
  });

  return {
    fornecedores: query.data ?? [],
    createForn:   create.mutate,
    updateForn:   update.mutate,
    deleteForn:   remove.mutate,
  };
};
